import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useUserOrganization } from '@/hooks/useUserOrganization'
import { useDashboardStore } from '../stores/dashboardStore'
import { useDashboardFilterArgs } from './useDashboardFilterArgs'
import type { DashboardTableTotals } from '../types/dashboard.types'

/**
 * Antall + sum for gjeldende filter (#934).
 *
 * Denne RPC-en skanner hele treffmengden — den er dyr, og blir dyrere for
 * hver ordre. Derfor kjører den bare når operatøren har trykket knappen:
 * `enabled` henger på `totalsRequested`, som enhver filterendring
 * nullstiller. Uten den koblingen ville et tall bli stående og beskrive en
 * mengde som ikke lenger er på skjermen.
 */
export function useDashboardTableTotals() {
  const { data: membership } = useUserOrganization()
  const orgId = membership?.organization_id

  const totalsRequested = useDashboardStore((s) => s.totalsRequested)
  const filterArgs = useDashboardFilterArgs(orgId)

  return useQuery({
    queryKey: ['dashboard-table-totals', filterArgs],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('delivery')
        .rpc('dashboard_table_orders_totals', filterArgs)

      if (error) {
        console.error('Dashboard totals error:', error)
        throw error
      }

      const row = (data || [])[0] as DashboardTableTotals | undefined
      return row ?? { total_count: 0, total_amount_sum: 0 }
    },
    enabled: !!orgId && totalsRequested,
    retry: 1,
    // Et eksplisitt hentet tall skal ikke refetches i bakgrunnen — da er vi
    // tilbake til et skann operatøren ikke har bedt om.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}
